'use strict';

var scrollAnimate = require('./scrollAnimate');

/**
 * Throttles a function to execute at most once per specified wait period.
 * @param {Function} fn - The function to throttle
 * @param {number} wait - The wait time in milliseconds
 * @returns {Function} The throttled function
 */
function throttle(fn, wait) {
    let timeout = null;
    return function () {
        if (!timeout) {
            const context = this;
            const args = arguments;
            timeout = setTimeout(() => {
                fn.apply(context, args);
                timeout = null;
            }, wait);
        }
    };
}

/**
 * Initializes the sticky add-to-cart component for product detail pages.
 * Sets up observers, event handlers, and UI elements for sticky cart functionality.
 */
function init() {
    if (window.matchMedia && !window.matchMedia('(min-width: 992px)').matches) return;

    const activeObservers = new Map();
    const $window = $(window);
    const $body = $('body');
    const $pdp = $('.product-detail');

    if (!$pdp.length) return;

    const originalButtonSelectors = [
        '.product-detail .add-to-cart',
        '.product-detail .add-to-cart-global'
    ];

    const buttonContainerSelectors = [
        '.product-detail .prices-add-to-cart-actions',
        '.product-detail .prices',
        '.product-detail [data-attr="color"]',
        '.product-detail [data-attr="size"]',
        '.product-detail .product-name',
        '.product-detail .out-of-stock'
    ];

    let scrollHandler;
    let suppressUntil = 0;

    /**
     * Gets the original add-to-cart button from the product detail page.
     * @returns {jQuery|null} The original button element or null if not found
     */
    function getOriginalButton() {
        for (let sel of originalButtonSelectors) {
            const $btn = $(sel);
            if ($btn.length) return $btn;
        }
        return null;
    }

    /**
     * Clones all attributes from a source element to a target jQuery element.
     * @param {HTMLElement} source - The source element to copy attributes from
     * @param {jQuery} target - The target jQuery element to copy attributes to
     */
    function cloneAttributes(source, target) {
        Array.from(source.attributes).forEach(attr => target.attr(attr.name, attr.value));
    }

    /**
     * Initializes the sticky add-to-cart button and sets up event handlers and observers.
     */
    function initializeStickyButton() {
        $('.sticky-container').remove();

        const originalButton = getOriginalButton();
        if (!originalButton || !originalButton.length) return;

        const stickyButton = $('<button></button>')
            .addClass(originalButton[0].className)
            .html(originalButton.html());
        cloneAttributes(originalButton[0], stickyButton);

        const stickyContainer = $('<div class="sticky-container"></div>');
        const stickyContent = $('<div class="container"></div>');
        const stickyRow = $('<div class="row col-12 sticky-content"></div>');
        const stickyLeft = $('<div class="sticky-left"></div>');
        const stickyRight = $('<div class="sticky-right"></div>');

        buildLeftContent(stickyLeft);
        buildRightContent(stickyRight, stickyButton);

        stickyRow.append(stickyLeft, stickyRight);
        stickyContent.append(stickyRow);
        stickyContainer.append(stickyContent);
        $body.append(stickyContainer);

        bindEvents(stickyContainer, stickyButton, originalButton);
        observeOriginalButton(originalButton, stickyButton);
        updateStickyButtonVisibility();

        if (scrollHandler) $window.off('scroll', scrollHandler);

        scrollHandler = throttle(updateStickyButtonVisibility, 100);
        $window.on('scroll', scrollHandler).on('resize', scrollHandler);

        buttonContainerSelectors.forEach(selector => {
            const $container = $(selector);
            if ($container.length) {
                activateObserver($container[0], () => {
                    initializeStickyButton();
                });
            }
        });
    }

    /**
     * Builds the left content section of the sticky container with product name, wishlist, prices, and out-of-stock info.
     * @param {jQuery} $left - The jQuery element representing the left container
     */
    function buildLeftContent($left) {
        const $name = $pdp.find('.product-name').first();
        const $wishlist = $pdp.find('.wishlist-item .wishlist-button').first();
        const $prices = $pdp.find('.prices').first();
        const $oos = $pdp.find('.out-of-stock').first();

        if ($name.length) {
            const $row = $('<div class="sticky-name-row"></div>')
                .append(`<div class="sticky-product-name">${$name.text()}</div>`);
            if ($wishlist.length) $row.append($wishlist.clone().addClass('sticky-wishlist'));
            $left.append($row);
        }

        if ($prices.length) {
            const $clone = $prices.clone().addClass('sticky-prices');
            $clone.find('.promo-value').prependTo($clone);
            $left.append($clone);
        }

        if ($oos.length) $left.append($oos.clone().addClass('sticky-out-of-stock'));
    }

    /**
     * Builds the right content section of the sticky container with color selector, size selector, and add-to-cart button.
     * @param {jQuery} $right - The jQuery element representing the right container
     * @param {jQuery} stickyButton - The sticky add-to-cart button element
     */
    function buildRightContent($right, stickyButton) {
        buildColorSelector($right);
        buildSizeSelector($right);
        $right.append(stickyButton);
    }

    /**
     * Builds the color selector component in the sticky container.
     * @param {jQuery} $container - The jQuery element representing the container
     */
    function buildColorSelector($container) {
        const $colorAttr = $pdp.find('[data-attr="color"]').first();
        if (!$colorAttr.length) return;

        const colorLabel = $colorAttr.find('.non-input-label').first().text() || 'Color';
        const $wrap = $('<div class="sticky-color"></div>');
        const $select = $('<div class="sticky-color-select"></div>');
        const $trigger = $('<button type="button" class="sticky-color-trigger custom-select form-control"></button>')
            .attr('aria-label', colorLabel);
        const $options = $('<div class="sticky-color-options"></div>');

        let selectedLabel = '';
        let selectedImg = '';

        $colorAttr.find('[data-attr-value]').each(function () {
            const $val = $(this);
            const url = $val.parent('button').attr('data-url') || $val.attr('data-url');
            const label = $val.attr('aria-label') || colorLabel;
            const swatchUrl = $val.css('background-image');
            const swatchMatch = swatchUrl && swatchUrl !== 'none' ? swatchUrl.match(/url\(["']?(.*?)["']?\)/) : null;
            const swatchImg = swatchMatch[1] || '';
            const isSelected = $val.hasClass('selected');

            const $btn = $('<button type="button"></button>')
                .addClass('sticky-color-item')
                .attr('data-url', url || '')
                .attr('aria-label', label)
                .toggleClass('is-selected', isSelected);

            if (swatchImg) $('<img class="sticky-color-img">').attr({ src: swatchImg, alt: label }).appendTo($btn);
            else $('<span class="sticky-color-swatch">').css('background-image', swatchUrl !== 'none' ? swatchUrl : '').appendTo($btn);

            if (isSelected) {
                selectedLabel = label;
                selectedImg = swatchImg;
            }

            $options.append($btn);
        });

        if (selectedImg) $('<img class="sticky-color-img">').attr({ src: selectedImg, alt: selectedLabel || colorLabel }).appendTo($trigger);
        else $('<span class="sticky-color-swatch">').appendTo($trigger);

        $select.append($trigger, $options);
        $wrap.append($select);
        $container.append($wrap);
    }

    /**
     * Builds the size selector component in the sticky container.
     * @param {jQuery} $container - The jQuery element representing the container
     */
    function buildSizeSelector($container) {
        const $sizeAttr = $pdp.find('[data-attr="size"]').first();
        if (!$sizeAttr.length) return;

        const sizeLabel = $sizeAttr.find('label').first().text();
        const $select = $('<select class="sticky-select sticky-size-select form-control custom-select"></select>')
            .append($('<option>').text(sizeLabel).val('').prop('disabled', true));

        $sizeAttr.find('[data-attr-value]').each(function () {
            const $val = $(this);
            const url = $val.attr('data-url');
            const $opt = $('<option>').val(url).text($.trim($val.text()));
            if ($val.hasClass('selected')) $opt.prop('selected', true);
            // if ($val.hasClass('disabled') || $val.attr('disabled')) $opt.prop('disabled', true);
            $select.append($opt);
        });

        $container.append($('<div class="sticky-size"></div>').append($select));
    }

    /**
     * Binds event handlers to the sticky container and its interactive elements.
     * @param {jQuery} container - The sticky container element
     * @param {jQuery} stickyButton - The sticky add-to-cart button
     * @param {jQuery} originalButton - The original add-to-cart button from the product detail page
     */
    function bindEvents(container, stickyButton, originalButton) {
        container.on('click', '.sticky-color-trigger', e => {
            e.preventDefault(); e.stopPropagation();
            $(e.currentTarget).closest('.sticky-color-select').toggleClass('is-open');
        });

        container.on('click', '.sticky-color-item', function () {
            const url = $(this).attr('data-url');
            if (!url) return;
            const $target = $pdp.find(`[data-attr="color"] button[data-url="${url}"]`);
            if ($target.length) $target[0].dispatchEvent(new MouseEvent('click', { bubbles: true }));
            $(this).closest('.sticky-color-select').removeClass('is-open');
        });

        container.on('change', '.sticky-size-select', function () {
            const url = $(this).val();
            if (!url) return;
            const $target = $pdp.find(`[data-attr="size"] button[data-url="${url}"]`);
            if ($target.length) $target[0].dispatchEvent(new MouseEvent('click', { bubbles: true }));
        });

        stickyButton.on('click', e => {
            if (stickyButton.hasClass('js-notify-me')) {
                scrollAnimate($pdp);
                setTimeout(() => {
                    $('#notify-me-form-email').trigger('focus');
                }, 200);
            }
            if (stickyButton.prop('disabled') || stickyButton.hasClass('disabled-btn')) return;
            e.preventDefault(); e.stopPropagation();
            suppressUntil = Date.now() + 2000;
            originalButton[0].dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        });

        $(document).off('click.stickyWishlist').on('click.stickyWishlist', '.sticky-wishlist', function (e) {
            e.preventDefault();
            e.stopPropagation();
            const $originalWishlist = $pdp.find('.wishlist-item .wishlist-button').first();
            if ($originalWishlist.length) {
                $originalWishlist[0].dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
            }
        });

        $(document).off('click.stickyColor').on('click.stickyColor', () => {
            $('.sticky-color-select').removeClass('is-open');
        });

        $('.minicart').on('cart:show', function (event, obj) {
            if (obj.show) {
                suppressUntil = Date.now() + 2000;
                $('.sticky-container').removeClass('is-visible');
            }
        });

        $(document).off('product:afterAddToCart.sticky').on('product:afterAddToCart.sticky', function () {
            suppressUntil = Date.now() + 2000;
            $('.sticky-container').removeClass('is-visible');
        });
    }

    /**
     * Updates the visibility of the sticky add-to-cart container based on scroll position and UI state.
     */
    function updateStickyButtonVisibility() {
        const originalButton = getOriginalButton();
        if (!originalButton) return;

        const rect = originalButton[0].getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const header = document.querySelector('header');
        const headerHeight = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
        const topOffset = headerHeight;
        const popoverVisible = $('.minicart .popover.show').length > 0;
        const footer = document.querySelector('.footer__navigation');
        const stickyEl = document.querySelector('.sticky-container');
        const stickyHeight = stickyEl ? Math.ceil(stickyEl.getBoundingClientRect().height) : 0;
        const footerRect = footer ? footer.getBoundingClientRect() : null;
        const footerOverlapsSticky = footerRect && stickyHeight > 0
            ? (footerRect.top <= (windowHeight - stickyHeight) && footerRect.bottom > 0)
            : false;
        const isHidden = !originalButton.is(':visible');
        const isCoveredByHeader = rect.top < topOffset && rect.bottom <= topOffset;
        const isBelowViewport = rect.top >= windowHeight;
        const shouldShow = !popoverVisible && !footerOverlapsSticky && (isHidden || isCoveredByHeader || isBelowViewport) && Date.now() > suppressUntil;
        $('.sticky-container').toggleClass('is-visible', shouldShow);
    }

    /**
     * Activates a MutationObserver on the target element and stores it in the activeObservers map.
     * @param {HTMLElement} target - The target element to observe
     * @param {Function} callback - The callback function to execute when mutations occur
     */
    function activateObserver(target, callback) {
        if (activeObservers.has(target)) return;
        const observer = new MutationObserver(callback);
        observer.observe(target, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
        activeObservers.set(target, observer);
    }

    /**
     * Observes changes to the original button and syncs them to the sticky button.
     * @param {jQuery} originalButton - The original add-to-cart button element
     * @param {jQuery} stickyButton - The sticky add-to-cart button element to sync
     */
    function observeOriginalButton(originalButton, stickyButton) {
        activateObserver(originalButton[0], () => {
            cloneAttributes(originalButton[0], stickyButton);
            stickyButton.html(originalButton.html());
            updateStickyButtonVisibility();
        });
    }

    initializeStickyButton();
}

module.exports = { init };
